import MappingsExplained from '@assets/mappings-explained.png';
import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatReceiveGap } from '@racemap/utilities/formattings/times';
import {
  labelFormatDistance,
  labelFormatPace,
  labelFormatVelocity,
} from '@racemap/utilities/formattings/units';
import { parseDateTimeSafe } from '@racemap/utilities/functions/datetime';
import {
  unitAwareDistance,
  unitAwarePace,
  unitAwareVelocity,
} from '@racemap/utilities/functions/units';
import type {
  AcceptedReadRust,
  PredictionDebugData,
  RejectedReadRust,
} from '@racemap/utilities/types/trackping';
import type { ReadVariants, Reader } from '@racemap/utilities/types/types';
import { UnitType } from '@racemap/utilities/types/units';
import { Popover, Space, Tooltip, Typography } from 'antd';
import { Timestamp, TooltipWithUTCDatetime } from 'src/components/BasicComponents';
import { IconExclamationTriangle } from 'src/components/BasicComponents/Icons';
import { HelperModal } from 'src/components/Modal';
import ReactJsonViewModal from 'src/components/ReatJSONViewModal';
import { useControlled } from '../../../lib/customHooks';
import { type Column, SimpleTable } from '../../BasicComponents/SimpleTable';
import { Switch } from '../../BasicComponents/Switch';
import { UserOrCustomerId } from '../UserOrCustomerId';
import { MappingOfReadVariantIcon } from './utils';

const { Title } = Typography;

interface PingsTableProps {
  starterDebugData: PredictionDebugData | null;
  unitType: UnitType;
  readInFocus: Reader | AcceptedReadRust | null;
  showRejectedReads: boolean;
  onShowRejectedPing: (arg0: boolean) => void;
}

export type ExtendedReadRust = (RejectedReadRust | AcceptedReadRust) & {
  acceptedPingIndex: string | null;
  id: string;
} & Record<string, unknown>;

function withIndex<T>(array: Array<T>): Array<T & { reason: string; acceptedPingIndex: string }> {
  return array.map((value, index) => ({
    reason: '',
    acceptedPingIndex: index.toString(),
    ...value,
  }));
}

export const ReadsTable = ({
  starterDebugData,
  readInFocus,
  unitType,
  showRejectedReads: controlledShowRejectedPings,
  onShowRejectedPing,
}: PingsTableProps): React.ReactElement => {
  const [showRejectedReads, setShowRejectedPings] = useControlled<boolean>({
    initialValue: false,
    controlledValue: controlledShowRejectedPings,
    onChange: onShowRejectedPing,
    name: 'ShowRejectedPings',
  });

  let data: Array<ExtendedReadRust> = [];
  if (starterDebugData != null) {
    data = (
      showRejectedReads
        ? [
            ...withIndex<AcceptedReadRust>(starterDebugData.acceptedReads),
            ...starterDebugData.rejectedReads,
          ]
        : withIndex<AcceptedReadRust>(starterDebugData.acceptedReads)
    )
      .map((aCP, index) => {
        const acceptedPingIndex: string | null =
          'acceptedPingIndex' in aCP ? (aCP.acceptedPingIndex as string) : null;
        return {
          id: String(index),
          acceptedPingIndex,
          ...aCP.read,
          ...aCP,
        };
      })
      .sort((a, b) => {
        const timeDiff = Date.parse(a.receivedAt) - Date.parse(b.receivedAt);
        if (timeDiff !== 0) return timeDiff;
        // fallback: sort by offset if times are equal
        const offsetA = typeof a.offset === 'number' ? a.offset : 0;
        const offsetB = typeof b.offset === 'number' ? b.offset : 0;
        return offsetA - offsetB;
      });
  }

  const columns = [];

  if (showRejectedReads) {
    columns.push({
      width: '50px',
      header: 'Id',
      dataKey: 'readId',
      key: 'readId',
      headerTitle: 'All mappings to a read have the same readId.',
      cellRenderer: (readId) => <ColoresSquareByReadId id={readId} />,
    } as Column<ExtendedReadRust, 'readId'>);
  } else {
    columns.push({
      width: '50px',
      header: 'Nr.',
      dataKey: 'id',
      key: 'index',
      cellClassName: 'center',
      headerTitle: 'Number of the accepted read',
      cellRenderer: (_, row): React.ReactElement => <span>{row.acceptedPingIndex}</span>,
    } as Column<ExtendedReadRust, 'id'>);
  }

  columns.push({
    width: '80px',
    header: 'Type',
    dataKey: 'variant',
    key: 'variant',
    cellClassName: 'center',
    headerTitle: 'Type (start/finish/normal) this read originates.',
    cellRenderer: (variant: ReadVariants): React.ReactElement => {
      return (
        <span style={{ marginRight: '6px', cursor: 'pointer' }}>
          <MappingOfReadVariantIcon variant={variant} />
        </span>
      );
    },
  } as Column<ExtendedReadRust, 'variant'>);

  columns.push({
    header: 'Reader',
    dataKey: 'readerId',
    key: 'timingName',
    cellClassName: 'center',
    headerTitle: 'Name and ID of the TrackBox or Decoder or Reader that recorded this read',
    cellRenderer: (readerId, row): React.ReactElement => (
      <div>{row.timingName ? `${row.timingName} - ${String(readerId)}` : String(readerId)}</div>
    ),
  } as Column<ExtendedReadRust, 'readerId'>);

  columns.push({
    header: 'Offset',
    dataKey: 'offset',
    key: 'offset',
    sortable: true,
    headerTitle: 'At this offset, racemap maps the reading onto the shadowtrack.',
    cellRenderer: (offset): React.ReactElement => {
      return (
        <div>
          {offset != null
            ? labelFormatDistance(unitAwareDistance(offset, unitType), unitType, 2, true)
            : '-'}
        </div>
      );
    },
  } as Column<ExtendedReadRust, 'offset'>);

  columns.push({
    header: 'Pace',
    dataKey: 'velocity',
    key: 'velocity',
    sortable: true,
    headerTitle: 'Pace calculated from this read to its predecessor.',
    cellRenderer: (velocity: number): React.ReactElement => {
      return (
        <Popover
          title={
            <tbody>
              <tr>
                <th style={{ width: '70px', fontWeight: 400 }} />
                <th style={{ width: '100px', fontWeight: 500 }}>Metric</th>
                <th style={{ width: '100px', fontWeight: 500 }}>Imperial</th>
                <th style={{ width: '100px', fontWeight: 500 }}>Nautical</th>
              </tr>
              <tr>
                <td style={{ fontWeight: 400 }}>Speed</td>
                <td>
                  {velocity != null
                    ? labelFormatVelocity(
                        unitAwareVelocity(3.6 * velocity, UnitType.METRIC),
                        UnitType.METRIC,
                        2,
                        true,
                      )
                    : '-'}
                </td>
                <td>
                  {velocity != null
                    ? labelFormatVelocity(
                        unitAwareVelocity(3.6 * velocity, UnitType.IMPERIAL),
                        UnitType.IMPERIAL,
                        2,
                        true,
                      )
                    : '-'}
                </td>
                <td>
                  {velocity != null
                    ? labelFormatVelocity(
                        unitAwareVelocity(3.6 * velocity, UnitType.NAUTICAL),
                        UnitType.NAUTICAL,
                        2,
                        true,
                      )
                    : '-'}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 400 }}>Pace</td>
                <td>
                  {velocity != null
                    ? labelFormatPace(
                        unitAwarePace(velocity, UnitType.METRIC),
                        UnitType.METRIC,
                        0,
                        true,
                      )
                    : '-'}
                </td>
                <td>
                  {velocity != null
                    ? labelFormatPace(
                        unitAwarePace(velocity, UnitType.IMPERIAL),
                        UnitType.IMPERIAL,
                        0,
                        true,
                      )
                    : '-'}
                </td>

                <td>
                  {velocity != null
                    ? labelFormatPace(
                        unitAwarePace(velocity, UnitType.NAUTICAL),
                        UnitType.NAUTICAL,
                        0,
                        true,
                      )
                    : '-'}
                </td>
              </tr>
            </tbody>
          }
        >
          {velocity != null
            ? labelFormatPace(unitAwarePace(velocity, unitType), unitType, 0, true)
            : '-'}
        </Popover>
      );
    },
  } as Column<ExtendedReadRust, 'velocity'>);

  columns.push({
    header: 'Read Time',
    dataKey: 'timestamp',
    key: 'timestamp',
    sortable: true,
    headerTitle: 'Time when this reading was recorded at the reader in UTC time.',
    cellRenderer: (timestamp): React.ReactElement => (
      <TooltipWithUTCDatetime datetime={timestamp}>
        <Space>
          <Timestamp
            time={timestamp}
            format="dayWithTime"
            title={`Read At: ${parseDateTimeSafe(timestamp)?.toUTC().toISO() || 'Invalid Date'}`}
          />
        </Space>
      </TooltipWithUTCDatetime>
    ),
  } as Column<ExtendedReadRust, 'timestamp'>);

  columns.push({
    header: 'Receive Time',
    dataKey: 'receivedAt',
    key: 'receivedAt',
    sortable: true,
    headerTitle:
      'Time when this reading was transmitted to racemap servers in UTC time and the difference in seconds. Lower is better. negative differences indicate errors or clocks not in sync',
    cellRenderer: (receivedAt, row): React.ReactElement => {
      let gap = '';
      if (row.timestamp != null) {
        const diff =
          0.001 * (Date.parse(receivedAt as string) - Date.parse(row.timestamp as string));
        gap = formatReceiveGap(diff);
      }

      return (
        <TooltipWithUTCDatetime datetime={receivedAt}>
          <Space>
            <Timestamp
              time={receivedAt}
              format="dayWithTime"
              title={`Received At: ${parseDateTimeSafe(receivedAt)?.toUTC().toISO() || 'Invalid Date'}`}
            />
            {gap}
          </Space>
        </TooltipWithUTCDatetime>
      );
    },
  } as Column<ExtendedReadRust, 'receivedAt'>);

  columns.push({
    header: 'Span in s',
    hint: 'Time from this measurement to the measurement before.',
    dataKey: 'timestamp',
    key: 'diffTime',
    headerTitle: 'Time from this accepted read to its predecessor.',
    cellRenderer: (timestamp, _row, index): React.ReactElement => {
      const lastPing = index > 0 ? data[index - 1] : null;
      const diffTime =
        typeof timestamp === 'string' && lastPing != null
          ? `${(0.001 * (Date.parse(timestamp) - Date.parse(lastPing.receivedAt))).toFixed(0)}`
          : '-';
      return <div>{diffTime}</div>;
    },
  } as Column<ExtendedReadRust, 'timestamp'>);

  showRejectedReads &&
    columns.push({
      header: 'User|Cust Id',
      hint: 'A reference to the source of the read.',
      dataKey: 'userId',
      key: 'userId',
      headerTitle:
        'A reference to the source of the read. Can be a Racemap User or a Race Result Customer ID.',
      cellRenderer: (userId: string): React.ReactElement => {
        return <UserOrCustomerId userOrCustomerId={userId} />;
      },
    } as Column<ExtendedReadRust, 'userId'>);

  showRejectedReads &&
    columns.push({
      width: '100px',
      header: 'Lot to track',
      hint: 'Distance of the read location to the shadowtrack in meters.',
      dataKey: 'distance',
      key: 'distance',
      headerTitle:
        'Distance of the read location to the shadowtrack in meters. Lower means that you placed your reader more accurate related to the track.',
      cellRenderer: (_, rowData): React.ReactElement => {
        return <>{rowData.mapping ? `${rowData.mapping.distance.toFixed(1)} m` : '-'}</>;
      },
    } as Column<ExtendedReadRust, 'distance'>);

  showRejectedReads &&
    columns.push({
      width: '80px',
      header: 'Warning',
      dataKey: 'reason',
      key: 'warning',
      cellRenderer: (reason: string | null) => {
        return reason ? <DropReasonTooltip reason={reason} /> : '-';
      },
    } as Column<ExtendedReadRust, 'reason'>);

  showRejectedReads &&
    columns.push({
      width: '80px',
      header: 'Details',
      dataKey: 'details',
      key: 'details',
      cellRenderer: (_, row) => {
        return <ReactJsonViewModal src={row} />;
      },
    } as Column<ExtendedReadRust, 'details'>);

  return (
    <>
      <Row>
        <Title level={4}>
          {showRejectedReads ? 'Accepted & rejected mappings' : 'Accepted mappings'}
          <MoveRightContainer>
            <HelperModal style={{ marginBottom: '4px' }} title="What is a mapping?" size="lg">
              A <strong>mapping</strong> is one possible position where a reading can be placed on
              the shadowtrack. On tracks with loops or crossings, a single reader can generate
              multiple mappings for different distances on the unfolded shadowtrack. The examples
              below show common scenarios:
              <Space orientation="vertical" style={{ marginTop: '10px' }}>
                <Space>
                  <ColoredCircle color={RacemapColors.BaseRed}>1</ColoredCircle>
                  <span>
                    <strong>Single mapping</strong> - The read (geo coordinates) fits in only one
                    spot on the shadowtrack. Useful for start or finish sections.
                  </span>
                </Space>
                <Space>
                  <ColoredCircle color={RacemapColors.BaseRed}>2</ColoredCircle>
                  <span>
                    <strong>Double mapping</strong> - The read fits in two possible spots. This
                    improves prediction accuracy using the same hardware, especially with RF-based
                    time-tracking devices like Raceresult Trackboxes.
                  </span>
                </Space>
                <Space>
                  <ColoredCircle color={RacemapColors.BaseRed}>3</ColoredCircle>
                  <span>
                    <strong>Quadro mapping</strong> - The read fits in four spots: two belong to a
                    steep corner (only one used), and two are far apart on separate loops. Here, one
                    reader produces three usable mappings, greatly boosting prediction quality.
                  </span>
                </Space>
              </Space>
              <img
                style={{ marginTop: '10px' }}
                width="100%"
                src={MappingsExplained}
                alt="Mappings Explained"
              />
            </HelperModal>
          </MoveRightContainer>
        </Title>
        <Switch
          value={showRejectedReads}
          onChange={() => {
            setShowRejectedPings(!showRejectedReads);
            onShowRejectedPing(showRejectedReads);
          }}
          label="Show more details and all mappings (accepted and rejected)"
        />
      </Row>

      <Container>
        <SimpleTable
          columns={columns as Array<Column<ExtendedReadRust>>}
          data={data}
          style={{ overflowX: 'auto' }}
          withSorting
          getRowStyle={(pingInfos) => {
            if (readInFocus != null && pingInfos.id === readInFocus.readerId) {
              return { backgroundColor: RacemapColors.LightGreen };
            }
            return {};
          }}
          defaultSortKey="receivedAt"
        />
      </Container>
    </>
  );
};

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Container = styled.div`
  th {
    text-align: center;
    :not(:last-of-type) {
      border-right: solid 1px ${RacemapColors.LightGray};
    }
  }
  td {
    text-align: end;
    :not(:last-of-type) {
      border-right: solid 1px ${RacemapColors.LightGray};
    }
  }
`;

const MoveRightContainer = styled.div`
  display: inline-block;
  scale: 0.8;  
  margin-left: 4px;
`;

const ColoredCircle = styled.div`
  width: 18px;
  height: 18px;
  background-color: ${({ color }) => color};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: white; /* ensure contrast */
`;

const ColoresSquareByReadId = ({ id }: { id: number | null }) => {
  // Define a color table with 10 visually distinct colors
  const colorTable = [
    '#e6194b', // red
    '#3cb44b', // green
    '#ffe119', // yellow
    '#4363d8', // blue
    '#f58231', // orange
    '#911eb4', // purple
    '#46f0f0', // cyan
    '#f032e6', // magenta
    '#bcf60c', // lime
    '#fabebe', // pink
  ];
  // Use modulo to cycle through colors for ids
  const backgroundColor = colorTable[(id ?? 0) % colorTable.length];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '100%' }}>
      <div
        style={{
          width: '16px',
          height: '16px',
          backgroundColor,
          borderRadius: '4px',
        }}
      />
      <span>{id}</span>
    </div>
  );
};

function DropReasonTooltip({
  reason,
}: {
  reason: string;
}): React.ReactElement {
  return (
    <Tooltip id="" title={reason} style={{ whiteSpace: 'pre', textAlign: 'left' }}>
      <IconExclamationTriangle style={{ color: RacemapColors.DangerRed }} />
    </Tooltip>
  );
}
